import { Groups, Endpoints, Gateways, GreasePump } from "../../../../Models/Assets/AssetTemplateReadDto";
import { applyGroupStatusToEndpoints } from "../../../Assets/Utilities/AssetConfigurationUtils";

export const ALL_ACCOUNTS_OPTION = { id: "", name: "All accounts" };

/**
 * Returns the name as-is (trimmed). No longer appends " (SiteName)".
 * Names must be unique within the site (validated separately).
 */
export function formatTemplateNameForBackend(
  name: string,
  _account: { id?: string; name?: string } | string | null | undefined
): string {
  return (name || "").trim();
}

/** Removes legacy " (SiteName)" suffix for display/validation compatibility. Preserves " (Copy)" / " (Cópia)". */
export function stripTemplateNameSuffix(name: string): string {
  let s = (name || "").trim();
  while (true) {
    const match = s.match(/\s+\(([^)]+)\)$/);
    if (!match) break;
    const suffixContent = match[1];
    if (/^(copy|cópia|copia)$/i.test(suffixContent)) break;
    s = s.substring(0, match.index).trim();
  }
  return s;
}

/** Normalize type value - e.g. "Hme" -> "HME", "locomotive" -> "Locomotive", "fixed plant" -> "FixedPlant". */
export function normalizeType(type: string | undefined): string {
  if (!type) return "";
  const normalized = type.toUpperCase().replace(/\s+/g, "");
  if (normalized === "HME") return "HME";
  if (normalized === "LOCOMOTIVE") return "Locomotive";
  if (normalized === "FIXEDPLANT") return "FixedPlant";
  return type;
}

/** Get display label for template/asset type (e.g. "FixedPlant" -> "Fixed Plant"). */
export function getTemplateTypeDisplay(type: string | undefined): string {
  if (!type) return "";
  const normalized = normalizeType(type);
  if (normalized === "FixedPlant") return "Fixed Plant";
  return normalized;
}

/** Template type options for DropDownList - must match ASSET_TEMPLATE_TYPES in AssetTemplateDetailsComponent. */
const TEMPLATE_TYPE_OPTIONS = [
  { text: "HME", value: "HME" },
  { text: "Locomotive", value: "Locomotive" },
  { text: "Fixed Plant", value: "FixedPlant" },
];

/** Convert API type string to DropDownList option object so Kendo can match initial value. */
export function getTemplateTypeOption(type: string | undefined): { text: string; value: string } | undefined {
  if (!type) return undefined;
  const normalized = normalizeType(type);
  return TEMPLATE_TYPE_OPTIONS.find((o) => o.value === normalized);
}

export interface TemplatePayloadInput {
  formData: {
    name: string;
    type: string;
    account?: any;
    image?: string;
  };
  imageBase64: string | null;
  hasGreasePumps: boolean;
  groups: Groups[];
  endpoints: Endpoints[];
  gateways: Gateways[];
  greasePumps: GreasePump[];
  useCurrentAccount: boolean;
  isAdministrationMode?: boolean;
  currentSiteId: string;
  currentSiteName: string;
}

/** Build the payload object sent to create/update template API. */
export function buildTemplatePayload(input: TemplatePayloadInput): Record<string, unknown> {
  const {
    formData,
    imageBase64,
    hasGreasePumps,
    groups,
    endpoints,
    gateways,
    greasePumps,
    useCurrentAccount,
    isAdministrationMode = false,
    currentSiteId,
    currentSiteName,
  } = input;

  const imageValue = imageBase64 ?? formData?.image ?? "";
  const imageWithPrefix =
    typeof imageValue === "string" && imageValue.startsWith("data:image")
    ? imageValue
    : imageValue
      ? `data:image/png;base64,${imageValue}`
      : "";

  const accountForPayload = useCurrentAccount
    ? { id: currentSiteId, name: currentSiteName }
    : formData.account;
  const siteId = isAdministrationMode
    ? ""
    : useCurrentAccount
      ? currentSiteId
      : typeof formData.account === "object"
        ? formData.account?.id
        : formData.account || "";

  // type: frontend may store {text, value} for dropdown; backend expects string (e.g. "HME", "Locomotive", "FixedPlant")
  const rawType = formData.type;
  const typeValue =
    typeof rawType === "object" && rawType !== null && "value" in rawType
      ? (rawType as { value: string }).value
      : (typeof rawType === "string" ? rawType : "") || "";

  const groupIdMap = new Map<string, string>();
  groups.forEach((group) => {
    const payloadId = (group.id ?? "").toString().startsWith("temp-") ? "" : (group.id ?? "");
    groupIdMap.set(group.id, payloadId);
  });

  return {
    name: formatTemplateNameForBackend(formData.name, accountForPayload),
    type: typeValue,
    siteId,
    image: imageWithPrefix,
    isGreasePumpConnectivityEnabled: hasGreasePumps || false,
    groups: groups.map((group) => ({
      id: (group.id ?? "").toString().startsWith("temp-") ? "" : (group.id ?? ""),
      name: group.name,
      xPos: group.xPos || 0,
      yPos: group.yPos || 0,
      status: group.status ?? "Active",
    })),
    // Status follows the group's: a disabled group must not keep Active endpoints, in the template or in the
    // assets the bulk update copies it to
    endpoints: applyGroupStatusToEndpoints(endpoints, groups).map((endpoint) => {
      const payloadGroupId = groupIdMap.get(endpoint.groupId) ?? endpoint.groupId ?? "";
      return {
        id: (endpoint.id ?? "").toString().startsWith("temp-endpoint-") ? "" : (endpoint.id ?? ""),
        groupId: payloadGroupId,
      name: endpoint.name,
      pointId: endpoint.pointId ?? 0,
      endpointDeviceId: endpoint.endpointDeviceId || "",
      xPos: endpoint.xPos || 0,
      yPos: endpoint.yPos || 0,
      status: endpoint.status ?? "Active",
    };
    }),
    gateways: gateways.map((gw) => ({
      id: gw.id || "",
      gatewayId: gw.gatewayId,
      name: gw.name,
      xPos: gw.xPos ?? 0,
      yPos: gw.yPos ?? 0,
      status: gw.status ?? "Active",
    })),
    greasePumps: hasGreasePumps
      ? greasePumps.map((pump) => ({
          id: pump.id,
          name: pump.name,
          channelNo: pump.channelNo || 0,
          xPos: pump.xPos ?? 0,
          yPos: pump.yPos ?? 0,
          status: pump.status ?? "Active",
        }))
      : [],
  };
}

export type EffectiveTemplate = Record<string, unknown> | null | undefined;

/** Compute form initial values from effectiveTemplate and options. */
export function getInitialValuesFromTemplate(
  effectiveTemplate: EffectiveTemplate,
  options: {
    showTemplateSourceChoice: boolean;
    isAdministrationMode?: boolean;
    currentSiteId: string;
    currentSiteName: string;
  }
): Record<string, unknown> {
  const { showTemplateSourceChoice, isAdministrationMode = false, currentSiteId, currentSiteName } = options;

  const resolveAccountValue = (accountValue: unknown) => {
    if (isAdministrationMode) return ALL_ACCOUNTS_OPTION;
    if (showTemplateSourceChoice) return { id: currentSiteId, name: currentSiteName };
    return accountValue;
  };

  if (effectiveTemplate) {
    let accountValue: unknown = (effectiveTemplate as any).account;
    if (accountValue === undefined || accountValue === null) {
      if ((effectiveTemplate as any).accountName === "") {
        accountValue = ALL_ACCOUNTS_OPTION;
      } else if (
        (effectiveTemplate as any).siteId !== undefined &&
        (effectiveTemplate as any).siteId !== null
      ) {
        accountValue =
          (effectiveTemplate as any).siteId === ""
            ? ALL_ACCOUNTS_OPTION
            : (effectiveTemplate as any).siteId;
      } else {
        accountValue = ALL_ACCOUNTS_OPTION;
      }
    } else if (
      accountValue === "" ||
      (typeof accountValue === "string" && accountValue === "")
    ) {
      accountValue = ALL_ACCOUNTS_OPTION;
    }

    const apiType = (effectiveTemplate as any).type;
    const typeForForm = getTemplateTypeOption(apiType) ?? (apiType ? normalizeType(apiType) : "");

    return {
      ...effectiveTemplate,
      name: stripTemplateNameSuffix((effectiveTemplate as any).name ?? ""),
      type: typeForForm,
      account: resolveAccountValue(accountValue),
      image: (effectiveTemplate as any).image || "",
      hasGreasePumps:
        (effectiveTemplate as any).isGreasePumpConnectivityEnabled ||
        (effectiveTemplate as any).hasGreasePumps ||
        false,
    };
  }

  return {
    name: "",
    type: "",
    hasGreasePumps: true,
    account: resolveAccountValue(null),
    image: "",
  };
}
